import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AdminNav } from "@/components/admin/AdminNav";
import { runSiteAudit, type SiteAuditResult } from "@/lib/site-audit.functions";
import { runSemrushAudit, type SemrushAuditResult, type SemrushTable } from "@/lib/semrush.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/security")({
  head: () => ({
    meta: [
      { title: "Admin | Security audit | Scott Cunningham" },
      { name: "description", content: "Security audit for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Security audit | Scott Cunningham" },
      { property: "og:description", content: "Security audit for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Security audit | Scott Cunningham" },
      { name: "twitter:description", content: "Security audit for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SecurityAuditPage,
});

type Status = "pass" | "warn" | "info";
type LiveStatus = "pass" | "warn" | "fail" | "info";
type FilterStatus = "pass" | "warn" | "fail" | "info";

type Control = {
  name: string;
  status: Status;
  detail: string;
  evidence?: { label: string; href: string }[];
};

type Section = {
  title: string;
  summary: string;
  controls: Control[];
};

const SECTIONS: Section[] = [
  {
    title: "Row-Level Security (RLS)",
    summary:
      "All 20 public tables have RLS enabled. Policies are scoped to auth.uid() or admin role; anon has no read access to user data.",
    controls: [
      {
        name: "RLS enabled on every public table",
        status: "pass",
        detail: "20 / 20 public tables enforce RLS. No USING (true) policies.",
      },
      {
        name: "Anon write restrictions",
        status: "pass",
        detail:
          "contact_submissions denies anon INSERT — writes go through a rate-limited server function with the service key.",
        evidence: [{ label: "contact.functions.ts", href: "https://github.com" }],
      },
      {
        name: "Role check uses SECURITY DEFINER function",
        status: "pass",
        detail: "has_role() prevents RLS recursion; roles live in user_roles, never on profiles.",
      },
      {
        name: "Profile visibility",
        status: "pass",
        detail: "profiles readable only by owner or admin (has_role check).",
      },
    ],
  },
  {
    title: "Storage policies",
    summary: "One public bucket (blog-images) for post cover art. All writes require admin role.",
    controls: [
      {
        name: "blog-images bucket",
        status: "info",
        detail: "Public read (intentional — post covers embedded in pages). Writes gated to admin via storage policy.",
      },
      {
        name: "No other public buckets",
        status: "pass",
        detail: "No user-uploaded or PII buckets exposed to anon.",
      },
    ],
  },
  {
    title: "Form & endpoint protection",
    summary: "Public forms use honeypots, per-IP + per-email rate limits, and server-side validation.",
    controls: [
      {
        name: "Contact form",
        status: "pass",
        detail:
          "Zod validation, honeypot field, 3-per-day cap per IP and per email, IP hashed with service key salt.",
      },
      {
        name: "Tool submissions",
        status: "pass",
        detail: "3-per-day daily quota + 8s burst window per hashed IP (tool_usage table).",
      },
      {
        name: "Admin sign-in",
        status: "pass",
        detail:
          "Session expires after 24 hours. Failed attempts logged to admin_login_attempts with rate limiting.",
      },
      {
        name: "Ask Scott chat",
        status: "pass",
        detail: "Per-IP usage caps + ban table for abuse (ask_scott_usage, ask_scott_bans).",
      },
    ],
  },
  {
    title: "Content Security Policy & headers",
    summary:
      "Per-request nonce with 'strict-dynamic'; no unsafe-inline on script-src. HSTS, CORP, COOP, X-Frame-Options DENY, Referrer-Policy set.",
    controls: [
      {
        name: "CSP: default-src 'none'",
        status: "pass",
        detail: "Deny by default. object-src 'none', frame-ancestors 'none', base-uri 'self', form-action 'self'.",
      },
      {
        name: "CSP: script-src nonce-based",
        status: "pass",
        detail: "Fresh nonce per request. No 'unsafe-inline' or data: on script-src.",
      },
      {
        name: "HSTS",
        status: "warn",
        detail:
          "Staged rollout at max-age=300 with includeSubDomains. Bump to 1 week → 1 month → 2 years + preload once all subdomains are verified https.",
      },
      {
        name: "Cross-Origin isolation",
        status: "pass",
        detail: "CORP: same-origin, COOP: same-origin, X-Content-Type-Options: nosniff.",
      },
      {
        name: "Permissions-Policy",
        status: "pass",
        detail: "All powerful features (camera, mic, geolocation, payment, USB, etc.) denied.",
      },
      {
        name: "Server fingerprinting",
        status: "pass",
        detail: "Server and X-Powered-By headers are stripped from every response in src/server.ts.",
      },
    ],
  },
  {
    title: "Secrets & keys",
    summary: "Service role key never leaves server. Publishable/anon key is safe in client bundles.",
    controls: [
      {
        name: "SUPABASE_SERVICE_ROLE_KEY server-only",
        status: "pass",
        detail: "Only imported inside .server.ts helpers, loaded via dynamic import in server functions.",
      },
      {
        name: "No secrets at module scope of shared files",
        status: "pass",
        detail: "process.env reads happen inside server handlers.",
      },
    ],
  },
  {
    title: "Email & DNS (out-of-band)",
    summary: "Verify SPF, DKIM, and DMARC at p=reject on scottcbusiness.com.",
    controls: [
      {
        name: "SPF / DKIM / DMARC",
        status: "info",
        detail: "Check at mxtoolbox.com. Managed in DNS, not in this codebase.",
        evidence: [
          { label: "MXToolbox DMARC check", href: "https://mxtoolbox.com/DMARC.aspx" },
        ],
      },
    ],
  },
  {
    title: "Caching & long-cache lifetime",
    summary:
      "Fingerprinted /assets/* and /fonts/* are force-served with 1-year immutable Cache-Control from src/server.ts, overriding any platform defaults.",
    controls: [
      {
        name: "Static assets: 1y immutable",
        status: "pass",
        detail: "Cache-Control: public, max-age=31536000, immutable on hashed assets; existing platform headers are overwritten.",
      },
      {
        name: "HTML: revalidate every request",
        status: "pass",
        detail: "SSR HTML sent no-cache so nonce and dynamic head tags stay fresh.",
      },
      {
        name: "Live check",
        status: "info",
        detail: "Run the audit below to fetch sample assets and confirm live headers.",
      },
    ],
  },
  {
    title: "HTML streaming",
    summary:
      "SSR HTML is streamed so the browser can parse and paint before the full payload arrives. Only the <head> is buffered to extract preload links and set the matching CSP nonce.",
    controls: [
      {
        name: "Streaming nonce injection",
        status: "pass",
        detail: "A TransformStream injects the per-request nonce into script/style tags as chunks arrive.",
      },
      {
        name: "Small head buffer",
        status: "pass",
        detail: "Only the first 64 KB of the response is held to discover preload links and the CSP nonce; the body streams immediately after.",
      },
    ],
  },
  {
    title: "SEO & structured data hygiene",
    summary:
      "Route-level titles / descriptions / canonicals via head(). JSON-LD includes Organization + Article schemas. Duplicate marquee markup is inert to crawlers.",
    controls: [
      {
        name: "Alt text on images",
        status: "pass",
        detail: "ResponsiveImg requires an alt prop at the type level.",
      },
      {
        name: "Blog Article schema",
        status: "pass",
        detail: "Article with nested Organization publisher + logo ImageObject on post routes.",
      },
      {
        name: "Heading hierarchy",
        status: "pass",
        detail: "Single H1 per page (case studies, blog posts, marketing routes).",
      },
      {
        name: "Live check",
        status: "info",
        detail: "Use the live audit to spot missing alt, invalid JSON-LD, or oversized DOM per page.",
      },
    ],
  },
];

const STATUS_STYLES: Record<Status, string> = {
  pass: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  warn: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  info: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
};

const LIVE_STATUS_STYLES: Record<"pass" | "warn" | "fail" | "info", string> = {
  pass: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  warn: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  fail: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30",
  info: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
};

function SecurityAuditPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterStatus | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user || !isAdmin) return null;

  const totals = SECTIONS.flatMap((s) => s.controls).reduce(
    (acc, c) => ({ ...acc, [c.status]: acc[c.status] + 1 }),
    { pass: 0, warn: 0, info: 0 } as Record<Status, number>,
  );
  const matchesFilter = (s: LiveStatus | Status) =>
    filter === null || (filter as string) === s;
  const toggle = (s: FilterStatus) => setFilter((cur) => (cur === s ? null : s));
  const chipClass = (s: FilterStatus, base: string) =>
    `rounded-full border px-3 py-1 transition ${base} ${
      filter === s ? "ring-2 ring-offset-2 ring-offset-background ring-current" : filter ? "opacity-50 hover:opacity-100" : ""
    }`;

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <AdminNav title="Security audit" />

      <p className="text-sm text-muted-foreground">
        Snapshot of RLS, storage, form protection, CSP, and secrets posture. Update this page when
        posture changes.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
        <button type="button" onClick={() => toggle("pass")} className={chipClass("pass", STATUS_STYLES.pass)}>
          {totals.pass} pass
        </button>
        <button type="button" onClick={() => toggle("warn")} className={chipClass("warn", STATUS_STYLES.warn)}>
          {totals.warn} watch
        </button>
        <button type="button" onClick={() => toggle("info")} className={chipClass("info", STATUS_STYLES.info)}>
          {totals.info} info
        </button>
        {filter && (
          <button
            type="button"
            onClick={() => setFilter(null)}
            className="rounded-full border border-border px-3 py-1 text-muted-foreground hover:text-foreground"
          >
            Clear filter
          </button>
        )}
      </div>

      <div className="mt-10 space-y-10">
        {SECTIONS.map((section) => {
          const controls = section.controls.filter((c) => matchesFilter(c.status));
          if (controls.length === 0) return null;
          return (
          <section key={section.title}>
            <h2 className="font-display text-2xl">{section.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{section.summary}</p>
            <div className="mt-4 divide-y divide-border rounded-md border border-border">
              {controls.map((c) => (
                <div key={c.name} className="flex flex-col gap-2 p-4 md:flex-row md:items-start md:justify-between">
                  <div className="md:pr-6">
                    <div className="font-medium">{c.name}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{c.detail}</p>
                    {c.evidence && c.evidence.length > 0 && (
                      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                        {c.evidence.map((e) => (
                          <li key={e.href}>
                            <a
                              href={e.href}
                              target="_blank"
                              rel="noreferrer"
                              className="underline hover:text-foreground"
                            >
                              {e.label} ↗
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <span
                    className={`inline-flex h-fit shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em] ${STATUS_STYLES[c.status]}`}
                  >
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
          );
        })}
        <LiveAuditPanel filter={filter} onToggleFilter={toggle} onClearFilter={() => setFilter(null)} />
        <SemrushPanel />
      </div>
    </div>
  );
}

function LiveAuditPanel({
  filter,
  onToggleFilter,
  onClearFilter,
}: {
  filter: FilterStatus | null;
  onToggleFilter: (s: FilterStatus) => void;
  onClearFilter: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SiteAuditResult | null>(null);
  const [origin, setOrigin] = useState("https://scottcbusiness.com");
  const matches = (s: LiveStatus) => filter === null || filter === s;
  const chipClass = (s: FilterStatus, base: string) =>
    `rounded-full border px-3 py-1 transition ${base} ${
      filter === s ? "ring-2 ring-offset-2 ring-offset-background ring-current" : filter ? "opacity-50 hover:opacity-100" : ""
    }`;

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const { data } = await supabase.auth.getSession();
      const accessToken = data.session?.access_token;
      if (!accessToken) throw new Error("Not signed in");
      const res = await runSiteAudit({ data: { accessToken, origin } });
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Live site audit</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Fetches sample public pages and asset headers, then flags SEO, structured data, alt text, DOM size, and cache lifetime issues with suggestions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm w-64"
            placeholder="https://scottcbusiness.com"
          />
          <button
            onClick={run}
            disabled={running}
            className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {running ? "Analyzing…" : result ? "Re-analyze" : "Run audit"}
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button type="button" onClick={() => onToggleFilter("pass")} className={chipClass("pass", LIVE_STATUS_STYLES.pass)}>{result.summary.pass} pass</button>
            <button type="button" onClick={() => onToggleFilter("warn")} className={chipClass("warn", LIVE_STATUS_STYLES.warn)}>{result.summary.warn} warn</button>
            <button type="button" onClick={() => onToggleFilter("fail")} className={chipClass("fail", LIVE_STATUS_STYLES.fail)}>{result.summary.fail} fail</button>
            <button type="button" onClick={() => onToggleFilter("info")} className={chipClass("info", LIVE_STATUS_STYLES.info)}>{result.summary.info} info</button>
            {filter && (
              <button type="button" onClick={onClearFilter} className="rounded-full border border-border px-3 py-1 text-muted-foreground hover:text-foreground">
                Clear filter
              </button>
            )}
            <span className="ml-auto rounded-full border border-border px-3 py-1 text-muted-foreground">
              ran {new Date(result.ranAt).toLocaleString()}
            </span>
          </div>

          {result.pages.map((page) => {
            const findings = page.findings.filter((f) => matches(f.status));
            if (findings.length === 0) return null;
            return (
            <div key={page.url} className="rounded-md border border-border">
              <div className="flex items-baseline justify-between border-b border-border px-4 py-2 text-sm">
                <a href={page.url} target="_blank" rel="noreferrer" className="font-mono truncate hover:underline">
                  {page.url}
                </a>
                <span className="text-xs text-muted-foreground">
                  HTTP {page.httpStatus} · {page.raw.domNodes} nodes · {Math.round(page.raw.htmlBytes / 1024)} KB
                </span>
              </div>
              <ul className="divide-y divide-border">
                {findings.map((f) => (
                  <li key={f.key} className="flex flex-col gap-1 px-4 py-3 md:flex-row md:items-start md:justify-between">
                    <div className="md:pr-6">
                      <div className="text-sm font-medium">{f.label}</div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{f.detail}</p>
                      {f.suggestion && (
                        <p className="mt-1 text-xs text-foreground/80">
                          <span className="font-mono uppercase tracking-wider text-[10px] text-muted-foreground">Fix</span>{" "}
                          {f.suggestion}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex h-fit shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em] ${LIVE_STATUS_STYLES[f.status]}`}
                    >
                      {f.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            );
          })}

          {result.cache.filter((c) => matches(c.status)).length > 0 && (
            <div className="rounded-md border border-border">
              <div className="border-b border-border px-4 py-2 text-sm font-medium">Cache lifetime (sampled assets)</div>
              <ul className="divide-y divide-border">
                {result.cache.filter((c) => matches(c.status)).map((c) => (
                  <li key={c.url} className="flex flex-col gap-1 px-4 py-3 md:flex-row md:items-start md:justify-between">
                    <div className="md:pr-6 min-w-0">
                      <div className="text-xs font-mono truncate">{c.url}</div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>
                      {c.cacheControl && (
                        <p className="mt-0.5 text-[10px] font-mono text-muted-foreground/80">Cache-Control: {c.cacheControl}</p>
                      )}
                    </div>
                    <span
                      className={`inline-flex h-fit shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em] ${LIVE_STATUS_STYLES[c.status]}`}
                    >
                      {c.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.headers && result.headers.checks.filter((c) => matches(c.status)).length > 0 && (
            <div className="rounded-md border border-border">
              <div className="flex items-baseline justify-between border-b border-border px-4 py-2 text-sm">
                <span className="font-medium">Security headers</span>
                <a href={result.headers.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-muted-foreground truncate hover:underline">
                  {result.headers.url}
                </a>
              </div>
              <ul className="divide-y divide-border">
                {result.headers.checks.filter((c) => matches(c.status)).map((c) => (
                  <li key={c.key} className="flex flex-col gap-1 px-4 py-3 md:flex-row md:items-start md:justify-between">
                    <div className="md:pr-6 min-w-0">
                      <div className="text-sm font-medium">{c.label}</div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>
                      {c.value && (
                        <p className="mt-0.5 text-[10px] font-mono text-muted-foreground/80 break-all">{c.value}</p>
                      )}
                      {c.suggestion && c.status !== "pass" && (
                        <p className="mt-1 text-xs text-foreground/80">
                          <span className="font-mono uppercase tracking-wider text-[10px] text-muted-foreground">Fix</span>{" "}
                          {c.suggestion}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex h-fit shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em] ${LIVE_STATUS_STYLES[c.status]}`}
                    >
                      {c.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.crawl && result.crawl.checks.filter((c) => matches(c.status)).length > 0 && (
            <div className="rounded-md border border-border">
              <div className="flex flex-col gap-1 border-b border-border px-4 py-2 text-sm md:flex-row md:items-baseline md:justify-between">
                <span className="font-medium">Crawlability (robots.txt & sitemap.xml)</span>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-mono text-muted-foreground">
                  <a href={result.crawl.robotsUrl} target="_blank" rel="noreferrer" className="hover:underline">{result.crawl.robotsUrl}</a>
                  <a href={result.crawl.sitemapUrl} target="_blank" rel="noreferrer" className="hover:underline">{result.crawl.sitemapUrl}</a>
                </div>
              </div>
              <ul className="divide-y divide-border">
                {result.crawl.checks.filter((c) => matches(c.status)).map((c) => (
                  <li key={c.key} className="flex flex-col gap-1 px-4 py-3 md:flex-row md:items-start md:justify-between">
                    <div className="md:pr-6 min-w-0">
                      <div className="text-sm font-medium">{c.label}</div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>
                      {c.suggestion && c.status !== "pass" && (
                        <p className="mt-1 text-xs text-foreground/80">
                          <span className="font-mono uppercase tracking-wider text-[10px] text-muted-foreground">Fix</span>{" "}
                          {c.suggestion}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex h-fit shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em] ${LIVE_STATUS_STYLES[c.status]}`}
                    >
                      {c.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SemrushTableView({ table, empty }: { table: SemrushTable | null; empty: string }) {
  if (!table || table.rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{empty}</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 text-left">
          <tr>
            {table.columnNames.map((c) => (
              <th key={c} className="px-3 py-2 font-mono uppercase tracking-wide">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i} className="border-t border-border">
              {table.columnNames.map((c) => (
                <td key={c} className="px-3 py-2">{row[c] ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SemrushPanel() {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SemrushAuditResult | null>(null);
  const [domain, setDomain] = useState("scottcbusiness.com");
  const [database, setDatabase] = useState("us");

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const { data } = await supabase.auth.getSession();
      const accessToken = data.session?.access_token;
      if (!accessToken) throw new Error("Not signed in");
      const res = await runSemrushAudit({ data: { accessToken, domain, database } });
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Semrush audit</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pulls domain authority, organic visibility, backlink profile, top keywords, and top anchor texts from Semrush via the connector gateway.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm w-56"
            placeholder="scottcbusiness.com"
          />
          <div className="relative">
            <select
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              className="appearance-none rounded-md border border-border bg-background px-3 py-1.5 pr-8 text-sm w-24"
              aria-label="Semrush database"
            >
              <option value="us">US</option>
              <option value="uk">UK</option>
              <option value="ca">CA</option>
              <option value="au">AU</option>
              <option value="de">DE</option>
              <option value="fr">FR</option>
              <option value="es">ES</option>
              <option value="it">IT</option>
              <option value="br">BR</option>
              <option value="in">IN</option>
              <option value="jp">JP</option>
              <option value="mx">MX</option>
              <option value="nl">NL</option>
              <option value="se">SE</option>
              <option value="sg">SG</option>
            </select>
            <svg
              className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <button
            onClick={run}
            disabled={running}
            className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {running ? "Fetching…" : result ? "Refresh" : "Run Semrush"}
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded-full border border-border px-3 py-1">domain: {result.domain}</span>
            <span className="rounded-full border border-border px-3 py-1">db: {result.database}</span>
            <span className="ml-auto rounded-full border border-border px-3 py-1">
              ran {new Date(result.ranAt).toLocaleString()}
            </span>
          </div>

          {result.errors.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
              <div className="font-medium">Partial results:</div>
              <ul className="mt-1 list-disc pl-5 space-y-0.5">
                {result.errors.map((e, i) => (<li key={i}>{e}</li>))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="font-display text-lg mb-2">Domain overview</h3>
            <SemrushTableView table={result.overview} empty="No overview data returned." />
          </div>
          <div>
            <h3 className="font-display text-lg mb-2">Backlink profile</h3>
            <SemrushTableView table={result.backlinks} empty="No backlink data returned." />
          </div>
          <div>
            <h3 className="font-display text-lg mb-2">Top organic keywords</h3>
            <SemrushTableView table={result.topKeywords} empty="No ranking keywords in this database." />
          </div>
          <div>
            <h3 className="font-display text-lg mb-2">Top backlink anchors</h3>
            <SemrushTableView table={result.topAnchors} empty="No anchor data returned." />
          </div>
        </div>
      )}
    </section>
  );
}